<template>
  <v-dialog v-model="dialog" width="680" @click:outside="cancel" :persistent="isLoading">
    <v-card>
      <v-card-title>
        <h3><strong>{{ $t('companyCodeBulkDialog.label.title') }}</strong></h3>
      </v-card-title>
      <v-card-text>
        <div class="ml-4 mb-2" v-if="message" style="color: #2e7d32">
          {{ message }}
        </div>
        <div class="ml-4" v-if="errorMessage" style="color: red">
          {{ errorMessage }}
        </div>
        <!-- 行ごとの誤りは件数が多くなりうるので、スクロールできる枠にまとめる -->
        <div class="error_box ml-4 mt-1" v-if="errorList.length > 0">
          <div v-for="(data, idx) in errorList" :key="idx" style="color: red">
            {{ data.message }}
          </div>
        </div>

        <v-container>
          <v-row>
            <v-col cols="12">
              <div class="text-subtitle-2 mb-1">{{ $t('companyCodeBulkDialog.label.file') }}</div>
              <input
                ref="fileInput"
                type="file"
                :accept="acceptedFileTypes"
                style="display: none"
                @change="onFileSelected"
              />
              <v-btn variant="outlined" :disabled="isLoading" @click="openFilePicker">
                {{ $t('common.action.upload') }}
              </v-btn>
              <span class="ml-3">{{ fileName }}</span>
            </v-col>
          </v-row>
          <!-- 「内容を確認」の結果。反映する前に追加・変更・削除を見せる（全件入れ替え） -->
          <v-row v-if="preview">
            <v-col cols="12">
              <div class="text-subtitle-2 mb-1">{{ $t('companyCodeBulkDialog.label.previewTitle') }}</div>
              <div class="mb-2">
                {{ $t('companyCodeBulkDialog.label.summary', {
                  added: preview.addedList.length, changed: preview.changedList.length,
                  deleted: preview.deletedList.length, unchanged: preview.unchangedCount, after: preview.afterCount }) }}
              </div>
              <div v-if="preview.deletedList.length > 0" class="delete_warn mb-2">
                {{ $t('companyCodeBulkDialog.message.deleteWarn', { count: preview.deletedList.length }) }}
              </div>
              <div v-if="!hasChange" class="mb-2">{{ $t('companyCodeBulkDialog.label.noChange') }}</div>

              <template v-if="preview.addedList.length > 0">
                <div class="diff_head added">{{ $t('companyCodeBulkDialog.label.added') }}（{{ preview.addedList.length }}）</div>
                <div class="diff_box">
                  <table class="diff">
                    <thead><tr>
                      <th>{{ $t('companyCodeBulkDialog.label.colCode') }}</th>
                      <th>{{ $t('companyCodeBulkDialog.label.colName') }}</th>
                      <th>{{ $t('companyCodeBulkDialog.label.colRemarks') }}</th>
                    </tr></thead>
                    <tbody>
                      <tr v-for="r in preview.addedList" :key="'a' + r.companyCode">
                        <td>{{ r.companyCode }}</td><td>{{ r.companyName }}</td><td>{{ r.remarks }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>

              <template v-if="preview.changedList.length > 0">
                <div class="diff_head changed">{{ $t('companyCodeBulkDialog.label.changed') }}（{{ preview.changedList.length }}）</div>
                <div class="diff_box">
                  <table class="diff">
                    <thead><tr>
                      <th>{{ $t('companyCodeBulkDialog.label.colCode') }}</th>
                      <th>{{ $t('companyCodeBulkDialog.label.colBefore') }}</th>
                      <th>{{ $t('companyCodeBulkDialog.label.colAfter') }}</th>
                    </tr></thead>
                    <tbody>
                      <tr v-for="r in preview.changedList" :key="'c' + r.companyCode">
                        <td>{{ r.companyCode }}</td>
                        <td>{{ joinNameRemarks(r.beforeName, r.beforeRemarks) }}</td>
                        <td>{{ joinNameRemarks(r.afterName, r.afterRemarks) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>

              <template v-if="preview.deletedList.length > 0">
                <div class="diff_head deleted">{{ $t('companyCodeBulkDialog.label.deleted') }}（{{ preview.deletedList.length }}）</div>
                <div class="diff_box">
                  <table class="diff">
                    <thead><tr>
                      <th>{{ $t('companyCodeBulkDialog.label.colCode') }}</th>
                      <th>{{ $t('companyCodeBulkDialog.label.colName') }}</th>
                      <th>{{ $t('companyCodeBulkDialog.label.colRemarks') }}</th>
                    </tr></thead>
                    <tbody>
                      <tr v-for="r in preview.deletedList" :key="'d' + r.companyCode">
                        <td>{{ r.companyCode }}</td><td>{{ r.companyName }}</td><td>{{ r.remarks }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <div class="format_box">
                <div class="text-subtitle-2 mb-1">{{ $t('companyCodeBulkDialog.label.formatTitle') }}</div>
                <ul class="pl-5">
                  <li>{{ $t('companyCodeBulkDialog.label.formatLine1') }}</li>
                  <li>{{ $t('companyCodeBulkDialog.label.formatLine2') }}</li>
                  <li>{{ $t('companyCodeBulkDialog.label.formatLine3') }}</li>
                  <li>{{ $t('companyCodeBulkDialog.label.formatLine4') }}</li>
                  <li>{{ $t('companyCodeBulkDialog.label.formatLine5') }}</li>
                </ul>
                <div class="text-caption mt-2">{{ $t('companyCodeBulkDialog.label.sample') }}</div>
                <table class="sample">
                  <thead>
                    <tr>
                      <th>{{ $t('companyCodeBulkDialog.label.sampleCode') }}</th>
                      <th>{{ $t('companyCodeBulkDialog.label.sampleName') }}</th>
                      <th>{{ $t('companyCodeBulkDialog.label.sampleRemarks') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>4550002</td><td>良品計画</td><td></td></tr>
                    <tr><td>4547315</td><td>良品計画</td><td>旧コード</td></tr>
                  </tbody>
                </table>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn text @click="cancel" :disabled="isLoading">{{ $t('companyCodeBulkDialog.action.close') }}</v-btn>
        <v-btn
          v-if="!preview"
          color="indigo darken-1"
          dark
          :loading="isLoading"
          :disabled="!file"
          @click="previewFile"
        >{{ $t('companyCodeBulkDialog.action.preview') }}</v-btn>
        <template v-else>
          <v-btn variant="outlined" :disabled="isLoading" @click="reselect">
            {{ $t('companyCodeBulkDialog.action.reselect') }}
          </v-btn>
          <v-btn
            :color="preview.deletedList.length > 0 ? 'red-darken-2' : 'indigo darken-1'"
            dark
            :loading="isLoading"
            :disabled="!hasChange"
            @click="importFile"
          >{{ $t('companyCodeBulkDialog.action.import') }}</v-btn>
        </template>
      </v-card-actions>
    </v-card>
    <!-- 誤りのあった行（件数・行番号・中身・理由）。誤りの行はCSVで出せる（JCIKAIHATSU-496 2026-11-03） -->
    <ImportResultDialog v-if="importResult" :title="$t('companyCodeBulkDialog.label.title')" :file-name="importResult.fileName"
      :result="importResult.result" :error-message="importResult.errorMessage" :handle-close="() => (importResult = null)" />
  </v-dialog>
</template>

<script>
import api from "@/apis/staff";
import constant from "@/utils/constant";
import utils from "@/utils";
import { isAcceptedImportFile } from "@/utils/importFileType";
import ImportResultDialog from "@/components/dialog/ImportResultDialog";

/**
 * 企業コード一括登録（Excel取込）
 *
 * 全件入れ替え（ファイルの内容がそのまま登録内容になる）。「内容を確認」で追加・変更・削除を
 * 見せてから「この内容で反映」で登録する（2026-10-21）。
 * 取り込むとサーバーの登録内容が変わるので、結果は handleImported で親に返し、
 * 親（設定画面）が一覧を読み直す。
 */
export default {
  components: { ImportResultDialog },
  props: {
    handleCancel: {
      type: Function,
      required: true,
    },
    // 取り込みに成功したら、サーバーが返した最新の一覧を渡して呼ぶ
    handleImported: {
      type: Function,
      required: true,
    },
  },

  computed: {
    fileName() {
      return this.file ? this.file.name : "";
    },
    hasChange() {
      const p = this.preview;
      return !!p && (p.addedList.length + p.changedList.length + p.deletedList.length) > 0;
    },
  },

  data() {
    return {
      dialog: true,
      isLoading: false,
      message: "",
      errorMessage: "",
      errorList: [],
      file: null,
      // 受け付けるファイルの種類（input の accept と、選んだときの確認に使う）
      acceptedFileTypes: ".xlsx",
      // 「内容を確認」の結果（追加・変更・削除と版数）。全件入れ替えなので、反映の前に必ず確かめる
      preview: null,
      // 誤りのあった行を見せる取込結果（{ fileName, result, errorMessage }）。null なら出さない
      importResult: null,
    };
  },

  methods: {
    clearMessages() {
      this.message = "";
      this.errorMessage = "";
      this.errorList = [];
    },
    cancel() {
      if (this.isLoading) return;
      this.handleCancel();
    },
    openFilePicker() {
      this.$refs.fileInput.click();
    },
    onFileSelected() {
      this.clearMessages();
      this.preview = null;
      const file = this.$refs.fileInput.files[0] ?? null;
      // 「すべてのファイル」に切り替えると別の種類も選べるので、選んだ時点で確かめて送らない
      if (file && !isAcceptedImportFile(file, this.acceptedFileTypes)) {
        this.resetFile();
        this.errorMessage = this.$t("common.message.selectXlsxFile");
        return;
      }
      this.file = file;
    },
    reselect() {
      this.clearMessages();
      this.preview = null;
      this.resetFile();
    },
    // 誤りのあった行が返ってきたら、取込結果の画面で件数と行を見せる
    showRowErrors(data) {
      const result = data.importResult;
      if (!result || !(result.rowErrors ?? []).length) return;
      this.importResult = { fileName: this.fileName, result, errorMessage: data.errorMessage };
    },
    joinNameRemarks(name, remarks) {
      return [name ?? "", remarks ? "（" + remarks + "）" : ""].join("") || "-";
    },
    // 登録はせず、今の登録内容との差分を受け取る
    previewFile() {
      if (!this.file) return;
      this.isLoading = true;
      this.clearMessages();
      const formData = new FormData();
      formData.append("file", this.file);
      formData.append("preview", "true");
      api
        .postFile("/config/company-code/bulk", formData)
        .then((response) => {
          if (response.data.status == constant.RESPONSE_STATUS.SUCCESS) {
            this.preview = response.data.results;
            return;
          }
          this.errorMessage = response.data.errorMessage;
          this.errorList = response.data.errorList ?? [];
          this.showRowErrors(response.data);
        })
        .catch((error) => {
          this.errorMessage = utils.getErrorMsg(error.response, this.$t);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    // 同じファイルをもう一度選べるよう、入力の値も消す
    resetFile() {
      this.file = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = null;
    },
    importFile() {
      if (!this.file) return;
      this.isLoading = true;
      this.clearMessages();

      const formData = new FormData();
      formData.append("file", this.file);
      // 確認したときの版数。確認のあとに企業コードが変わっていれば、サーバーが反映を断る
      formData.append("expectedVersion", String(this.preview.version));

      api
        .postFile("/config/company-code/bulk", formData)
        .then((response) => {
          if (response.data.status == constant.RESPONSE_STATUS.SUCCESS) {
            const results = response.data.results ?? {};
            this.message = this.$t("companyCodeBulkDialog.message.success", {
              count: results.importedCount ?? 0,
              total: (results.companyCodeList ?? []).length,
            });
            this.preview = null;
            this.resetFile();
            this.handleImported(results.companyCodeList ?? [], results.version);
            return;
          }
          // 誤りがあったときは1件も登録されていない。直して選び直してもらう
          this.errorMessage = response.data.errorMessage;
          this.errorList = response.data.errorList ?? [];
          this.showRowErrors(response.data);
        })
        .catch((error) => {
          this.errorMessage = utils.getErrorMsg(error.response, this.$t);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.error_box {
  max-height: 160px;
  overflow: auto;
  padding: 6px 10px;
  border: solid 1px #ef9a9a;
  border-radius: 4px;
  background: #fff5f5;
}
.delete_warn {
  font-size: 0.85rem;
  font-weight: bold;
  color: #c62828;
}
.diff_head {
  margin-top: 8px;
  font-size: 0.85rem;
  font-weight: bold;
  &.added { color: #2e7d32; }
  &.changed { color: #ef6c00; }
  &.deleted { color: #c62828; }
}
.diff_box {
  max-height: 180px;
  overflow: auto;
  border: solid 1px #e0e0e0;
  border-radius: 4px;
}
.diff {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  th,
  td {
    border-bottom: solid 1px #eeeeee;
    padding: 2px 8px;
    text-align: left;
  }
  th {
    position: sticky;
    top: 0;
    background: #f5f5f5;
  }
}
.format_box {
  padding: 10px 14px;
  border: solid 1px #e0e0e0;
  border-radius: 4px;
  background: #fafafa;
  font-size: 0.875rem;
}
.sample {
  border-collapse: collapse;
  margin-top: 4px;
  th,
  td {
    border: solid 1px #bdbdbd;
    padding: 2px 14px;
    text-align: left;
  }
  th {
    background: #eeeeee;
  }
}
</style>
